import { Box, Divider, Typography } from "@mui/material";
import { useCallback, useReducer, useRef } from "react";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import { isLocalDev } from "../../../../utils/environment";
import { ValidDataService } from "../../../../service/validDataService";
import useAbortController from "../../../../hooks/useAbortController";
import { DEFAULT_PAGE_SIZE } from "../../../../utils";
import useDialogModal from "../../../../hooks/useDialogModal";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import { toast } from "react-toastify";
import validLookup from "../../../../utils/validLookup";
import ValidAccessoryInputForm from "./validAccessoryInputForm";
import ValidAccessoryEditForm from "./validAccessoryEditForm";
import EditIcon from "@mui/icons-material/Edit";
import { useValidInventoryAccessoryColumns } from "./useValidInventoryAccessoryColumns";

export default function Accessory() {
    const [ValidAccessoryInputDialog, showValidAccessoryInputDialog] = useDialogModal(ValidAccessoryInputForm);
    const [ValidAccessoryEditDialog, showValidAccessoryEditDialog] = useDialogModal(ValidAccessoryEditForm);
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);
    const columns = useValidInventoryAccessoryColumns()
    const [signal, abortRequest] = useAbortController();
    const lastQueryRef = useRef({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0, filters: [], sortBy: [] });
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    const onAddRecordClicked = () => {
        showValidAccessoryInputDialog();
    }

    const handleRowEdit = (rowData) => {
        showValidAccessoryEditDialog(null, { recordToEdit: rowData });
    }

    const handleRowCopy = (rowData) => {
        // Implement copy functionality if needed
    }

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        lastQueryRef.current = { pageSize, pageIndex, filters, sortBy };
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        ValidDataService.getInventoryDataWithLimit(from, pageSize, filters, sortBy, 'accessories', signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, []);

    const deleteRecordHandler = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await ValidDataService.deleteInventoryRecord(rowids.map(x => parseInt(x, 10)));
                const { code, message } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ data: updateList });
                } else {
                    toast.error("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    const recordAddHandler = () => {
        const { pageSize, pageIndex, filters, sortBy } = lastQueryRef.current;
        fetchData({ pageSize, pageIndex, filters, sortBy });
    }

    const recordEditHandler = () => {
        const { pageSize, pageIndex, filters, sortBy } = lastQueryRef.current;
        fetchData({ pageSize, pageIndex, filters, sortBy });
    }

    return (
        <Box>
            <Typography variant="h4">Accessories</Typography>
            <Divider sx={{ mb: 2 }} />
            <EnhancedTable
                columns={columns}
                addRecordHandler={onAddRecordClicked}
                deleteRecordHandler={deleteRecordHandler}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                selectionMode="multi"
                loading={state.loading}
                actions={[
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                ]}
            />
            <ValidAccessoryInputDialog onRecordAdded={recordAddHandler} />
            <ValidAccessoryEditDialog onRecordUpdated={recordEditHandler} />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    )
}